<template>
  <div class="booking-page-root">
    <!-- Split Layout Wrapper -->
    <div class="booking-layout-wrapper">
      <!-- LEFT SIDEBAR: Desktop Showcase & Summary -->
      <div class="booking-showcase-sidebar">
        <div class="sidebar-image-overlay"></div>

        <div class="sidebar-content-wrapper">
          <v-btn elevation="0" class="custom-back-btn" @click="exitAlibey" fab small>
            <v-icon color="black">mdi-arrow-left</v-icon>
          </v-btn>

          <div class="sidebar-brand text-center">
            <v-img
              class="mx-auto mb-4"
              contain
              max-height="45px"
              src="https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_BLACK.png"></v-img>
            <span class="location-tag">EXCLUSIVE CLUB RESORT</span>
            <h2 class="main-event-title">Ali Bey Manavgat</h2>
            <p class="sub-event-location">Antalya, Türkei</p>
          </div>

          <!-- Live Booking Card Summary -->
          <div class="live-summary-card animate-blur-in">
            <h3 class="summary-card-title">
              <v-icon color="primary" class="mr-2">mdi-card-text-outline</v-icon>
              DEINE RESERVIERUNG
            </h3>

            <div class="summary-details">
              <!-- Dates summary -->
              <div class="summary-item">
                <v-icon color="primary">mdi-calendar-range</v-icon>
                <div class="item-text">
                  <span class="item-label">Reisezeitraum</span>
                  <span class="item-value" v-if="bookingDateRange && bookingDateRange.length >= 2">
                    {{ formattedBookingDateRange }} ({{ duration }} Nächte)
                  </span>
                  <span class="item-value placeholder" v-else>Noch nicht gewählt</span>
                </div>
              </div>

              <!-- Persons summary -->
              <div class="summary-item">
                <v-icon color="primary">mdi-account-group</v-icon>
                <div class="item-text">
                  <span class="item-label">Teilnehmer</span>
                  <span class="item-value" v-if="form.persons && form.persons.length > 0">
                    {{ form.persons.length }} Person(en)
                    <div class="persons-sublist">
                      <span v-for="(p, i) in form.persons" :key="i" class="person-tag">
                        {{ p.firstName || "Gast " + (i + 1) }}
                      </span>
                    </div>
                  </span>
                  <span class="item-value placeholder" v-else>Keine Personen</span>
                </div>
              </div>

              <!-- Selected Room summary -->
              <div class="summary-item">
                <v-icon color="primary">mdi-bed-king</v-icon>
                <div class="item-text">
                  <span class="item-label">Zimmer-Kategorie</span>
                  <span class="item-value" v-if="selectedRoom">
                    {{ selectedRoom.subtitle ? selectedRoom.subtitle + " " : "" }}{{ selectedRoom.title }}
                  </span>
                  <span class="item-value placeholder" v-else-if="currentActiveSlide === 0"> Wird im nächsten Schritt gewählt </span>
                  <span class="item-value placeholder text-warning" v-else> Bitte Zimmer wählen </span>
                </div>
              </div>
            </div>

            <v-divider class="my-4 rgba-divider"></v-divider>

            <!-- Live Price Calculation -->
            <div class="summary-price-block">
              <div class="price-header">
                <span class="price-label">Voraussichtlicher Gesamtpreis:</span>
                <span class="price-subtext" v-if="selectedRoom">*inkl. Sportpaket, zzgl. Flug</span>
              </div>
              <div class="price-amount" v-if="selectedRoom">
                {{ formatPrice(Math.round(selectedRoom.possibility.price * duration + aerobicPrice * adultCount)) }} CHF
              </div>
              <div class="price-amount placeholder-price" v-else>-- CHF</div>
            </div>
          </div>

          <!-- Footer trust badge -->
          <div class="sidebar-trust-footer">
            <v-icon color="success" class="mr-1" small>mdi-shield-check</v-icon>
            <span>Kostenlose Stornierung bis 90 Tage vor Abreise</span>
          </div>
        </div>
      </div>

      <!-- RIGHT WORKSPACE: Steps and Swiper Form -->
      <div class="booking-workspace">
        <!-- Mobile Header -->
        <div class="mobile-header">
          <v-btn icon @click="exitAlibey" class="mr-2">
            <v-icon color="black">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="mobile-brand-title">Ali Bey Reservierung</div>
        </div>

        <!-- Horizontal Step Tracker -->
        <div class="booking-progress-tracker">
          <div class="progress-step" :class="{ active: currentActiveSlide === 0, completed: currentActiveSlide > 0 }">
            <div class="step-badge">
              <v-icon v-if="currentActiveSlide > 0" color="white" small>mdi-check</v-icon>
              <v-icon v-else :color="currentActiveSlide === 0 ? 'white' : 'grey darken-1'" small>mdi-account-group-outline</v-icon>
            </div>
            <span class="step-label">Personen & Reisezeit</span>
          </div>
          <div class="progress-line" :class="{ completed: currentActiveSlide > 0 }"></div>

          <div
            class="progress-step"
            :class="{ active: currentActiveSlide === 1, completed: currentActiveSlide > 1, pending: currentActiveSlide < 1 }">
            <div class="step-badge">
              <v-icon v-if="currentActiveSlide > 1" color="white" small>mdi-check</v-icon>
              <v-icon v-else :color="currentActiveSlide === 1 ? 'white' : 'grey darken-1'" small>mdi-bed-king-outline</v-icon>
            </div>
            <span class="step-label">Zimmerauswahl</span>
          </div>
          <div class="progress-line" :class="{ completed: currentActiveSlide > 1 }"></div>

          <div class="progress-step" :class="{ active: currentActiveSlide === 2, pending: currentActiveSlide < 2 }">
            <div class="step-badge">
              <v-icon :color="currentActiveSlide === 2 ? 'white' : 'grey darken-1'" small>mdi-checkbox-marked-circle-outline</v-icon>
            </div>
            <span class="step-label">Bestätigung</span>
          </div>
        </div>

        <!-- Form Swiper Container -->
        <div class="workspace-swiper-container">
          <swiper ref="aliBeySwiper" :options="aliBeySwiperOptions" class="booking-swiper">
            <!-- Slide 0: Persons & Dates -->
            <swiper-slide class="booking-slide">
              <div class="slide-content">
                <h2 class="slide-title">LOS GEHT'S! 🔥<br />Wer ist dabei?</h2>

                <v-form v-if="form.persons.length" ref="personForm" lazy-validation class="person-form">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <v-menu
                        ref="menu"
                        v-model="rangePicker"
                        :close-on-content-click="false"
                        :return-value.sync="bookingDateRange"
                        transition="scale-transition"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <div class="date-selector-card animate-blur-in" v-bind="attrs" v-on="on">
                            <div class="date-section">
                              <div class="date-details">
                                <span class="date-title">HINREISE</span>
                                <span class="date-value">{{ formatStartDate() }}</span>
                              </div>
                            </div>

                            <div class="duration-badge" v-if="bookingDateRange && bookingDateRange.length >= 2">
                              <v-icon small color="primary">mdi-moon-waning-crescent</v-icon>
                              <span>{{ duration }} Nächte</span>
                            </div>

                            <div class="date-section text-right">
                              <div class="date-details">
                                <span class="date-title">RÜCKREISE</span>
                                <span class="date-value">{{ formatEndDate() }}</span>
                              </div>
                            </div>
                          </div>
                        </template>
                        <v-date-picker locale="de-DE" min="2027-05-27" max="2027-06-12" no-title range v-model="bookingDateRange" color="primary">
                          <v-spacer></v-spacer>
                          <v-btn text color="primary" @click="rangePicker = false">Abbrechen</v-btn>
                          <v-btn text color="primary" @click="$refs.menu.save(bookingDateRange)">OK</v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>

                  <div class="person-cards-container">
                    <div v-for="(person, index) in form.persons" :key="index" class="glass-person-card animate-blur-in">
                      <div class="person-card-header">
                        <div class="person-avatar">
                          <v-icon color="primary">mdi-account</v-icon>
                        </div>
                        <div class="person-header-text">
                          <span class="person-title">Gast {{ index + 1 }}</span>
                          <span class="person-subtitle" v-if="index === 0">Hauptbucher</span>
                          <span class="person-subtitle" v-else>Mitreisender</span>
                        </div>
                        <v-spacer></v-spacer>
                        <v-btn v-if="form.persons.length > 1" @click="deletePerson(index)" icon class="delete-person-btn" small>
                          <v-icon color="red lighten-1">mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </div>

                      <div class="person-card-body">
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              outlined
                              dense
                              hide-details="auto"
                              class="premium-input"
                              v-model="person.firstName"
                              :rules="requiredField"
                              label="Vorname">
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              outlined
                              dense
                              hide-details="auto"
                              class="premium-input"
                              v-model="person.lastName"
                              :rules="requiredField"
                              label="Nachname">
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-menu
                              :ref="'birthDayPicker' + index"
                              v-model="person.birthDayPicker.activeMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="auto">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  outlined
                                  dense
                                  hide-details="auto"
                                  class="premium-input"
                                  @click="setPickerToYear(index)"
                                  v-model="person.birthDayPicker.date"
                                  label="Geburtstag"
                                  prepend-inner-icon="mdi-cake-variant"
                                  readonly
                                  :rules="requiredField"
                                  v-bind="attrs"
                                  v-on="on"></v-text-field>
                              </template>
                              <v-date-picker
                                color="primary"
                                locale="de-CH"
                                v-model="person.birthDayPicker.date"
                                :active-picker.sync="activeBirthDayPicker"
                                :max="new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)"
                                min="1920-01-01"
                                @change="saveDatePicker($event, index)"></v-date-picker>
                            </v-menu>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-select
                              outlined
                              dense
                              hide-details="auto"
                              class="premium-input"
                              v-model="person.shirtSize"
                              :items="tshirtSizes"
                              label="T-Shirt Grösse"
                              prepend-inner-icon="mdi-tshirt-crew"
                              :rules="requiredField"></v-select>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </div>

                  <!-- NEU: Empfohlen durch Feld -->
                  <v-row class="mb-6">
                    <v-col cols="12">
                      <v-text-field
                        outlined
                        dense
                        hide-details="auto"
                        class="premium-input animate-blur-in"
                        v-model="form.recommendedBy"
                        label="Empfohlen durch (optional)"
                        prepend-inner-icon="mdi-account-heart-outline">
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <div class="add-person-container">
                    <div class="dashed-add-btn animate-blur-in" @click="addNewPerson">
                      <v-icon color="primary" class="mr-2">mdi-plus-circle-outline</v-icon>
                      <span>Mitreisenden hinzufügen</span>
                    </div>
                  </div>
                </v-form>

                <div class="slide-actions">
                  <v-btn text color="primary" @click="exitAlibey" large>Zurück</v-btn>
                  <v-btn class="next-step-btn" color="primary" x-large rounded @click="slideToSelection">Angebote ansehen</v-btn>
                </div>
              </div>
            </swiper-slide>

            <!-- Slide 1: Room Selection -->
            <swiper-slide class="booking-slide">
              <div class="slide-content">
                <h2 class="slide-title">WÄHLE DEIN PARADIES 🌴</h2>

                <div class="room-selection-container animate-blur-in" v-if="filteredRooms && filteredRooms.length > 0">
                  <div
                    v-for="(room, index) in filteredRooms"
                    :key="index"
                    class="luxury-room-card"
                    :class="{ 'selected-glow': room.selected }"
                    @click="selectRoom(index)">
                    <div class="room-image-wrapper">
                      <v-img height="220px" :src="room.img" cover class="room-image">
                        <!-- Floating details tag on image -->
                        <div class="room-size-tag">
                          <v-icon color="white" small class="mr-1">mdi-ruler-square</v-icon>
                          <span>{{
                            room.title.toLowerCase().includes("familie") || room.subtitle?.toLowerCase().includes("familie") ? "40 m²" : "26 m²"
                          }}</span>
                        </div>

                        <!-- Selection check overlay -->
                        <div class="room-selected-badge" v-if="room.selected">
                          <v-icon color="white" small>mdi-check</v-icon>
                        </div>
                      </v-img>
                    </div>

                    <div class="room-info-wrapper">
                      <div class="room-header">
                        <span class="room-category-label">{{ room.subtitle || "Premium" }}</span>
                        <h3 class="room-card-title">{{ room.title }}</h3>
                      </div>

                      <div class="room-card-price">
                        <div class="price-box">
                          <span class="price-value"
                            >{{ formatPrice(Math.round(room.possibility.price * duration + aerobicPrice * adultCount)) }} CHF</span
                          >
                          <span class="price-period">für {{ duration }} Nächte / {{ form.persons.length }} Pers.</span>
                        </div>
                        <div class="price-daily">
                          <span class="daily-value">{{ Math.round(room.possibility.price + (aerobicPrice * adultCount) / duration) }} CHF</span>
                          <span class="daily-label">pro Tag *</span>
                        </div>
                      </div>

                      <div class="details-divider"></div>

                      <div class="details-trigger-row" @click.stop="showEquipment(index)">
                        <span class="details-text">Ausstattung & Inklusivleistungen</span>
                        <v-icon color="primary" class="details-arrow-icon" :class="{ rotated: room.showEquipment }"> mdi-chevron-down </v-icon>
                      </div>

                      <!-- Expandable equipment grid with icons -->
                      <v-expand-transition>
                        <div v-show="room.showEquipment" class="room-equipment-expanded">
                          <div class="equipment-grid">
                            <div v-for="(equip, i) in room.equipment" :key="i" class="equip-item">
                              <v-icon color="primary" class="mr-2" small>{{ getEquipmentIcon(equip) }}</v-icon>
                              <span>{{ equip }}</span>
                            </div>
                          </div>
                        </div>
                      </v-expand-transition>

                      <div class="card-select-action">
                        <v-btn
                          block
                          rounded
                          elevation="0"
                          :color="room.selected ? 'primary' : 'grey lighten-4'"
                          class="select-btn"
                          :class="{ 'selected-btn': room.selected }">
                          {{ room.selected ? "Ausgewählt" : "Dieses Zimmer wählen" }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="no-results animate-blur-in">
                  <v-icon size="64" class="mb-4" color="grey lighten-1">mdi-magnify-remove-outline</v-icon>
                  <p class="font-weight-bold grey--text text--darken-2">Für diese Konstellation wurden leider keine Zimmer gefunden.</p>
                  <p class="text-caption grey--text">Bitte passe deine Personenanzahl oder das Alter der Kinder an.</p>
                </div>

                <div class="slide-actions">
                  <v-btn text color="primary" @click="slideTo(0)" large>Zurück</v-btn>
                  <v-btn class="next-step-btn" color="primary" x-large rounded :disabled="!selectedRoom" @click="confirmRoom"
                    >Auswahl bestätigen</v-btn
                  >
                </div>
              </div>
            </swiper-slide>

            <!-- Slide 2: Checkout / Confirmation -->
            <swiper-slide class="booking-slide">
              <div class="slide-content" v-if="selectedRoom">
                <h2 class="slide-title">FAST GESCHAFFT! 🚀</h2>

                <div class="confirmation-stage animate-blur-in">
                  <div class="confirmation-grid">
                    <!-- Summary Receipt Card -->
                    <div class="receipt-card">
                      <div class="receipt-header text-center">
                        <div class="success-ring-glow">
                          <v-icon color="white" size="36">mdi-calendar-check</v-icon>
                        </div>
                        <h3 class="receipt-title">Zusammenfassung</h3>
                        <p class="receipt-subtitle">Bitte überprüfe deine Angaben sorgfältig</p>
                      </div>

                      <div class="receipt-body">
                        <div class="receipt-section">
                          <h4 class="receipt-section-title">Reisedetails</h4>
                          <div class="receipt-row">
                            <span class="row-label">Resort:</span>
                            <span class="row-value bold-value">Ali Bey Club Manavgat</span>
                          </div>
                          <div class="receipt-row">
                            <span class="row-label">Zeitraum:</span>
                            <span class="row-value">{{ formattedBookingDateRange }}</span>
                          </div>
                          <div class="receipt-row">
                            <span class="row-label">Dauer:</span>
                            <span class="row-value">{{ duration }} Nächte</span>
                          </div>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <div class="receipt-section">
                          <h4 class="receipt-section-title">Gäste</h4>
                          <div v-for="(person, idx) in form.persons" :key="idx" class="receipt-person-row">
                            <div class="person-receipt-info">
                              <v-icon small color="primary" class="mr-2">mdi-account-circle</v-icon>
                              <span class="receipt-person-name">{{ person.firstName }} {{ person.lastName }}</span>
                            </div>
                            <span class="receipt-person-details">T-Shirt Grösse: {{ person.shirtSize }}</span>
                          </div>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <div class="receipt-section">
                          <h4 class="receipt-section-title">Zimmerkategorie</h4>
                          <div class="receipt-room-box">
                            <v-img height="70px" width="100px" :src="selectedRoom.img" class="rounded-lg mr-4" cover></v-img>
                            <div class="receipt-room-details">
                              <span class="receipt-room-cat">{{ selectedRoom.subtitle || "Premium" }}</span>
                              <span class="receipt-room-name">{{ selectedRoom.title }}</span>
                            </div>
                          </div>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <!-- Final Invoice Breakdown -->
                        <div class="invoice-breakdown">
                          <div class="invoice-row">
                            <span class="invoice-label">Zimmerpreis ({{ duration }} Nächte):</span>
                            <span class="invoice-value">{{ formatPrice(Math.round(selectedRoom.possibility.price * duration)) }} CHF</span>
                          </div>
                          <div class="invoice-row">
                            <span class="invoice-label">Sport- & Eventpaket ({{ adultCount }}x):</span>
                            <span class="invoice-value">{{ formatPrice(Math.round(aerobicPrice * adultCount)) }} CHF</span>
                          </div>
                          <v-divider class="my-3"></v-divider>
                          <div class="invoice-row total-row">
                            <span class="invoice-total-label">Gesamtpreis:</span>
                            <div class="invoice-total-block text-right">
                              <span class="invoice-total-value"
                                >{{ formatPrice(Math.round(selectedRoom.possibility.price * duration + aerobicPrice * adultCount)) }} CHF</span
                              >
                              <span class="invoice-tax-info">*inkl. MwSt., zzgl. Flugkosten</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Trust Panel -->
                    <div class="trust-benefits-panel">
                      <h3 class="panel-headline">Deine Buchungsvorteile</h3>
                      <div class="benefit-card-item">
                        <div class="benefit-icon-ring"><v-icon color="success">mdi-shield-check-outline</v-icon></div>
                        <div class="benefit-texts">
                          <h4>Kostenfreie Stornierung</h4>
                          <p>Bis 90 Tage vor Abreise kannst du die Reservierung komplett kostenfrei stornieren.</p>
                        </div>
                      </div>
                      <div class="benefit-card-item">
                        <div class="benefit-icon-ring"><v-icon color="primary">mdi-hours-24</v-icon></div>
                        <div class="benefit-texts">
                          <h4>24/7 Vor-Ort Support</h4>
                          <p>Unser Team ist während der gesamten Eventwoche rund um die Uhr für dich da.</p>
                        </div>
                      </div>
                      <div class="benefit-card-item">
                        <div class="benefit-icon-ring"><v-icon color="primary">mdi-glass-cocktail</v-icon></div>
                        <div class="benefit-texts">
                          <h4>Premium Ultra All Inclusive</h4>
                          <p>Alle Speisen, Snacks, Premium- und Markengetränke sind vollumfänglich im Preis enthalten.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="checkout-actions-wrapper">
                    <v-btn text color="primary" @click="slideTo(1)" large class="checkout-back-btn">
                      <v-icon left>mdi-chevron-left</v-icon> Zurück zur Zimmerwahl
                    </v-btn>
                    <v-btn
                      class="ultimate-checkout-btn"
                      color="primary"
                      x-large
                      rounded
                      elevation="12"
                      :loading="bookingLoading"
                      @click="createBooking">
                      <v-icon left class="mr-2 animate-bounce-horizontal">mdi-rocket-launch-outline</v-icon>
                      DEFINITIV RESERVIEREN
                    </v-btn>
                  </div>
                </div>
              </div>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </div>

    <v-snackbar color="error" v-model="showSnackBar" timeout="3000" centered>
      {{ snackBarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import api from "../../api"
import { Swiper, SwiperSlide, directive } from "vue-awesome-swiper"
import { mapState, mapActions } from "vuex"
import "swiper/css/swiper.css"

export default {
  name: "aliBeyBooking",
  components: { Swiper, SwiperSlide },
  directives: { swiper: directive },
  data() {
    return {
      currentActiveSlide: 0,
      bookingLoading: false,
      userBirthday: null,
      faultyBirthday: false,
      showSnackBar: false,
      adultCount: 0,
      snackBarMessage: "Fehler!",
      aerobicPrice: 119,
      activeBirthDayPicker: "",
      bookingDateRange: ["2027-05-29", "2027-06-05"],
      tshirtSizes: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
      rangePicker: false,
      childrenAge: [
        { start: 0, end: 2 },
        { start: 0, end: 6 },
        { start: 0, end: 12 },
        { start: 3, end: 12 },
        { start: 7, end: 12 },
      ],
      form: {
        persons: [],
        recommendedBy: null,
      },
      requiredField: [(v) => !!v || "Dies ist ein Pflichtfeld"],
      standardRooms: [
        {
          title: "Standardzimmer",
          selected: false,
          showEquipment: false,
          img: "/img/einmalig/alibeyStandardRoom.jpg",
          equipment: [
            "26 m2",
            "WLAN",
            "SAT LCD TV",
            "Musikkanäle",
            "Telefon",
            "Minibar",
            "Klimaanlage",
            "Safe",
            "Dusche / WC",
            "Föhn",
            "Keramikboden",
            "Balkon",
            "Ultra All inclusive",
          ],
          possibilities: [
            { adult: 1, children: { count: 0 }, price: 194.0 * 1.05 * 1.05 },
            { adult: 1, children: { count: 1, age: 1 }, price: 194.0 * 1.05 * 1.05 },
            { adult: 1, children: { count: 1, age: 4 }, price: 258.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 0 }, price: 258.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 1, age: 0 }, price: 258.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 1, age: 3 }, price: 350.0 * 1.05 * 1.05 },
            { adult: 3, children: { count: 0 }, price: 350.0 * 1.05 * 1.05 },
          ],
        },
        {
          title: "Standardzimmer",
          subtitle: "Superior",
          selected: false,
          showEquipment: false,
          img: "/img/einmalig/alibeySuperiorStandardRoom.jpg",
          equipment: [
            "26 m2",
            "WLAN",
            "SAT LCD TV",
            "Musikkanäle",
            "Telefon",
            "Minibar",
            "Klimaanlage",
            "Safe",
            "Dusche / WC",
            "Föhn",
            "Marmorboden",
            "Balkon",
            "Ultra All inclusive",
          ],
          possibilities: [
            { adult: 1, children: { count: 0 }, price: 216.0 * 1.05 * 1.05 },
            { adult: 1, children: { count: 1, age: 0 }, price: 216.0 * 1.05 * 1.05 },
            { adult: 1, children: { count: 1, age: 3 }, price: 286.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 0 }, price: 286.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 1, age: 0 }, price: 286.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 1, age: 3 }, price: 356.0 * 1.05 * 1.05 },
            { adult: 3, children: { count: 0 }, price: 356.0 * 1.05 * 1.05 },
          ],
        },
      ],
      familyRooms: [
        {
          title: "Familienzimmer",
          selected: false,
          img: "/img/einmalig/alibeyFamilyRoom.jpg",
          showEquipment: false,
          equipment: [
            "40 m2",
            "WLAN",
            "SAT LCD TV",
            "Musikkanäle",
            "Telefon",
            "Minibar",
            "Klimaanlage",
            "Safe",
            "Dusche / WC",
            "Föhn",
            "Keramikboden",
            "Balkon",
            "Ultra All inclusive",
          ],
          possibilities: [
            { adult: 2, children: { count: 0 }, price: 385.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 1, age: 2 }, price: 385.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 2, age: 2 }, price: 385.0 * 1.05 * 1.05 },
            { adult: 3, children: { count: 0 }, price: 385.0 * 1.05 * 1.05 },
            { adult: 3, children: { count: 1, age: 2 }, price: 385.0 * 1.05 * 1.05 },
            { adult: 4, children: { count: 0 }, price: 465.0 * 1.05 * 1.05 },
          ],
        },
        {
          title: "Familienzimmer",
          subtitle: "superior",
          selected: false,
          img: "/img/einmalig/alibeySuperiorFamilyRoom.jpg",
          showEquipment: false,
          equipment: [
            "40 m2",
            "WLAN",
            "SAT LCD TV",
            "Musikkanäle",
            "Telefon",
            "Minibar",
            "Klimaanlage",
            "Safe",
            "Dusche / WC",
            "Föhn",
            "Marmorboden",
            "Balkon",
            "Ultra All inclusive",
          ],
          possibilities: [
            { adult: 2, children: { count: 0 }, price: 459.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 1, age: 2 }, price: 459.0 * 1.05 * 1.05 },
            { adult: 2, children: { count: 2, age: 2 }, price: 459.0 * 1.05 * 1.05 },
            { adult: 3, children: { count: 0 }, price: 459.0 * 1.05 * 1.05 },
            { adult: 3, children: { count: 1, age: 2 }, price: 459.0 * 1.05 * 1.05 },
            { adult: 4, children: { count: 0 }, price: 559.0 * 1.05 * 1.05 },
          ],
        },
      ],
      filteredRooms: [],
    }
  },
  created() {
    if (this.navigationVisible) this.toggleNavigationBarVisibility()
    this.initialSetup()
  },
  beforeDestroy() {
    if (!this.navigationVisible) this.toggleNavigationBarVisibility()
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText", "toggleNavigationBarVisibility"]),
    initialSetup() {
      api.getUser().then((data) => {
        this.faultyBirthday = false
        if (
          !this.$moment(data.user.geburtstag, "YYYY-MM-DD", true).isValid() ||
          !this.$moment(data.user.geburtstag, "YYYY-MM-DD").isBefore("2010-05-24") ||
          data.user.geburtstag === "--"
        ) {
          this.faultyBirthday = true
          data.user.geburtstag = null
        }
        this.form.persons = []
        this.form.persons.push({
          email: data.user.email,
          firstName: data.user.vorname,
          lastName: data.user.nachname,
          birthDayPicker: { activeMenu: false, date: data.user.geburtstag },
          shirtSize: null,
        })
      })
    },
    exitAlibey() {
      if (!this.navigationVisible) this.toggleNavigationBarVisibility()
      this.$router.push("/alibey")
    },
    saveBirthday() {
      if (this.$moment(this.userBirthday, "YYYY-MM-DD", true).isValid()) {
        api.setUserBirthday(this.userBirthday).then(() => {
          this.initialSetup()
        })
      } else {
        alert("Bitte gebe ein gültiges Geburtsdatum ein")
      }
    },
    formatPrice(x, places = 2, country = "de-CH") {
      return Number(x || 0).toLocaleString(country, { maximumFractionDigits: places, minimumFractionDigits: places })
    },
    scrollToTop() {
      const el = this.$el.querySelector(".swiper-slide-active .slide-content")
      if (el) el.scrollTo({ top: 0, behavior: "smooth" })
    },
    selectRoom(index) {
      this.filteredRooms.forEach((room, i) => {
        this.$set(room, "selected", index === i)
      })
    },
    showEquipment(index) {
      this.$set(this.filteredRooms[index], "showEquipment", !this.filteredRooms[index].showEquipment)
    },
    deletePerson(index) {
      if (this.form.persons.length > 1) {
        this.form.persons.splice(index, 1)
      } else {
        this.showSnackBarWithMessage("Mindestens eine Person muss bestehen bleiben.")
      }
    },
    formIsValid() {
      return this.$refs.personForm.validate()
    },
    saveDatePicker(date, index) {
      this.$refs["birthDayPicker" + index][0].save(date)
    },
    createBooking() {
      this.bookingLoading = true

      const bookingData = {
        adults: this.adultCount,
        form: this.form,
        duration: this.duration,
        dateRange: this.bookingDateRange,
        room: this.selectedRoom,
      }

      const newListEntry = {
        category: "alibey",
        content: bookingData,
      }

      api
        .createListEntry(newListEntry)
        .then(() => {
          alert("Deine Buchung wurde gespeichert. Die Rechnung werden wir Dir in den nächsten Tagen zustellen. Wir freuen uns auf Dich.")
          this.$router.push({ name: "Home" })
        })
        .finally(() => {
          this.bookingLoading = false
        })
    },
    showSnackBarWithMessage(message) {
      this.snackBarMessage = message
      this.showSnackBar = true
    },
    addNewPerson() {
      if (this.form.persons.length >= 4) {
        this.showSnackBarWithMessage("Maximal 4 Personen pro Buchung.")
      } else {
        this.form.persons.push({ firstName: null, lastName: null, birthDayPicker: { activeMenu: false, date: null }, shirtSize: null })
      }
    },
    setPickerToYear(index) {
      if (this.form.persons[index]) {
        setTimeout(() => (this.activeBirthDayPicker = "YEAR"), 50)
      }
    },
    slideToSelection() {
      if (this.$moment(this.bookingDateRange[1]).isBefore(this.$moment(this.bookingDateRange[0]))) {
        return this.showSnackBarWithMessage("Das End-Datum kann nicht vor dem Start-Datum liegen.")
      }
      if (this.formIsValid()) {
        this.calculateOptions()
        this.slideTo(1)
        this.$nextTick(() => {
          this.scrollToTop()
        })
      } else {
        this.showSnackBarWithMessage("Bitte fülle alle Pflichtfelder korrekt aus.")
      }
    },
    confirmRoom() {
      if (this.selectedRoom) {
        this.slideTo(2)
        this.scrollToTop()
      }
    },
    calculateOptions() {
      this.filteredRooms = []
      let adults = 0,
        children = { count: 0, age: null }
      this.form.persons.forEach((p) => {
        const age = this.$moment(this.bookingDateRange[0]).diff(p.birthDayPicker.date, "years")
        if (age > 12) adults++
        else {
          children.count++
          children.age = age
        }
      })
      if (this.form.persons.length <= 3) {
        this.standardRooms.forEach((room) => {
          const poss = room.possibilities.find(
            (p) =>
              p.adult === adults &&
              children.count === p.children.count &&
              (children.count !== 1 ||
                (this.childrenAge[p.children.age].start <= children.age && this.childrenAge[p.children.age].end >= children.age)),
          )
          if (poss) this.filteredRooms.push({ ...room, possibility: poss })
        })
      }
      if (this.form.persons.length >= 2) {
        this.familyRooms.forEach((room) => {
          const poss = room.possibilities.find((p) => p.adult === adults && p.children.count === children.count)
          if (poss) this.filteredRooms.push({ ...room, possibility: poss })
        })
      }
      this.adultCount = adults
    },
    slideTo(slide) {
      this.$refs.aliBeySwiper.$swiper.slideTo(slide, 500, false)
      this.currentActiveSlide = slide
    },
    formatStartDate() {
      if (!this.bookingDateRange || !this.bookingDateRange[0]) return "Wählen..."
      return this.$moment(this.bookingDateRange[0]).format("DD.MM.YYYY")
    },
    formatEndDate() {
      if (!this.bookingDateRange || !this.bookingDateRange[1]) return "Wählen..."
      return this.$moment(this.bookingDateRange[1]).format("DD.MM.YYYY")
    },
    getEquipmentIcon(equip) {
      const e = equip.toLowerCase()
      if (e.includes("m2") || e.includes("m²")) return "mdi-ruler-square"
      if (e.includes("wlan") || e.includes("wifi")) return "mdi-wifi"
      if (e.includes("tv") || e.includes("sat")) return "mdi-television"
      if (e.includes("musik")) return "mdi-music-box-outline"
      if (e.includes("telefon")) return "mdi-phone-in-talk-outline"
      if (e.includes("minibar") || e.includes("kühlschrank")) return "mdi-liquor"
      if (e.includes("klima")) return "mdi-air-conditioner"
      if (e.includes("safe")) return "mdi-safe-square-outline"
      if (e.includes("dusche") || e.includes("bad") || e.includes("wc")) return "mdi-shower"
      if (e.includes("föhn")) return "mdi-hair-dryer"
      if (e.includes("keramik") || e.includes("marmor") || e.includes("boden")) return "mdi-texture"
      if (e.includes("balkon")) return "mdi-balcony"
      if (e.includes("all inclusive") || e.includes("ultra")) return "mdi-glass-cocktail"
      return "mdi-check"
    },
  },
  computed: {
    ...mapState("navigationStoreNotPersisted", ["navigationVisible"]),
    formattedBookingDateRange() {
      if (!this.bookingDateRange || this.bookingDateRange.length < 2) return ""
      const [start, end] = this.bookingDateRange.map((d) => this.$moment(d).format("DD.MM.YY"))
      return `${start} - ${end}`
    },
    duration() {
      if (!this.bookingDateRange || this.bookingDateRange.length < 2) return 0
      return this.$moment(this.bookingDateRange[1]).diff(this.$moment(this.bookingDateRange[0]), "days")
    },
    selectedRoom() {
      return this.filteredRooms.find((room) => room.selected)
    },
    aliBeySwiperOptions() {
      return { allowTouchMove: false, pagination: { el: ".swiper-pagination", dynamicBullets: true } }
    },
  },
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap");

.booking-page-root {
  height: 100vh;
  width: 100vw;
  background-color: #f8fafc;
  color: #0f172a;
  font-family: "Poppins", sans-serif;
  overflow: hidden;
  position: relative;
  letter-spacing: -0.2px;
}

/* Layout Wrapper */
.booking-layout-wrapper {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

/* Left Sidebar Showcase */
.booking-showcase-sidebar {
  width: 35%;
  min-width: 380px;
  max-width: 450px;
  height: 100%;
  position: relative;
  background-image: url("https://app.wellcomefit.ch/img/einmalig/alibey/slider/alibeySlider6.jpg");
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  z-index: 10;
  box-shadow: 10px 0 30px rgba(0, 0, 0, 0.15);
  transition: all 0.5s ease;

  .sidebar-image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(15, 23, 42, 0.95) 100%);
    z-index: 1;
  }

  .sidebar-content-wrapper {
    position: relative;
    z-index: 2;
    padding: 40px 32px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    justify-content: space-between;
  }
}

.custom-back-btn {
  align-self: flex-start;
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 24px;

  &:hover {
    transform: translateX(-4px) scale(1.05);
    background-color: #ffffff !important;
  }
}

.sidebar-brand {
  margin-bottom: 32px;
  .location-tag {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 2px;
    color: white;
    text-transform: uppercase;
  }

  .main-event-title {
    font-weight: 800;
    font-size: 2.2rem;
    color: #ffffff;
    line-height: 1.1;
    margin-top: 4px;
    text-transform: uppercase;
  }

  .sub-event-location {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 300;
    margin-bottom: 0;
  }
}

/* Live Summary Card inside Sidebar */
.live-summary-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  margin-bottom: auto;

  .summary-card-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 1px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
  }
}

.summary-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .v-icon {
    font-size: 1.25rem !important;
    margin-top: 2px;
  }

  .item-text {
    display: flex;
    flex-direction: column;

    .item-label {
      font-size: 0.7rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.5);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .item-value {
      font-size: 0.9rem;
      font-weight: 500;
      color: #ffffff;

      &.placeholder {
        color: rgba(255, 255, 255, 0.35);
        font-style: italic;
      }
      &.text-warning {
        color: #fb8c00;
        font-weight: 700;
      }
    }
  }
}

.persons-sublist {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;

  .person-tag {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    padding: 2px 10px;
    border-radius: 50px;
    font-size: 0.75rem;
    font-weight: 600;
  }
}

.rgba-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.summary-price-block {
  margin-top: 16px;
  .price-header {
    display: flex;
    flex-direction: column;

    .price-label {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.6);
      font-weight: 600;
    }

    .price-subtext {
      font-size: 0.65rem;
      color: rgba(255, 255, 255, 0.4);
    }
  }

  .price-amount {
    font-size: 1.8rem;
    font-weight: 900;
    color: white;
    margin-top: 4px;

    &.placeholder-price {
      color: rgba(255, 255, 255, 0.25);
    }
  }
}

.sidebar-trust-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  margin-top: 24px;
}

/* Right Workspace */
.booking-workspace {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f8fafc;
}

.mobile-header {
  display: none;
}

/* Step Progress Tracker */
.booking-progress-tracker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 40px;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  z-index: 5;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.01);

  .progress-step {
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s ease;

    &.active {
      .step-badge {
        background-color: var(--v-primary-base);
        border-color: var(--v-primary-base);
        box-shadow: 0 0 15px rgba(var(--v-primary-base), 0.4);
        transform: scale(1.08);
      }
      .step-label {
        color: #0f172a;
        font-weight: 700;
      }
    }

    &.completed {
      .step-badge {
        background-color: #10b981;
        border-color: #10b981;
      }
      .step-label {
        color: #0f172a;
        font-weight: 600;
      }
    }

    &.pending {
      opacity: 0.5;
      .step-badge {
        background-color: #f1f5f9;
        border-color: #cbd5e1;
      }
      .step-label {
        color: #64748b;
      }
    }
  }

  .step-badge {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }

  .step-label {
    font-size: 0.85rem;
    font-weight: 600;
    transition: all 0.3s ease;
  }

  .progress-line {
    flex: 1;
    height: 2px;
    background-color: #e2e8f0;
    margin: 0 20px;
    transition: all 0.4s ease;

    &.completed {
      background-color: #10b981;
    }
  }
}

/* Workspace Scroll Area & Swiper Container */
.workspace-swiper-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.booking-swiper {
  height: 100%;
  width: 100%;
}

.booking-slide {
  height: 100%;
  width: 100%;
  overflow-y: auto;
}

.slide-content {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 40px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.slide-title {
  font-size: 2.2rem;
  font-weight: 850;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 32px;
  line-height: 1.15;
  letter-spacing: -1px;
  color: #0f172a;
}

/* Custom Premium Date Card */
.date-selector-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 18px 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.02);
  margin-bottom: 28px;

  &:hover {
    border-color: var(--v-primary-base);
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(15, 23, 42, 0.05);
  }

  .date-section {
    display: flex;
    align-items: center;
    gap: 16px;

    .v-icon {
      font-size: 1.6rem !important;
    }
  }

  .date-details {
    display: flex;
    flex-direction: column;

    .date-title {
      font-size: 0.65rem;
      font-weight: 700;
      color: #64748b;
      letter-spacing: 1.5px;
    }

    .date-value {
      font-size: 1.05rem;
      font-weight: 750;
      color: #0f172a;
      margin-top: 2px;
    }
  }

  .duration-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(var(--v-primary-base), 0.08);
    padding: 6px 14px;
    border-radius: 50px;
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--v-primary-base);
    border: 1px dashed rgba(var(--v-primary-base), 0.3);
  }
}

/* Glass Person Form Cards */
.person-cards-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
}

.glass-person-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.02);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
    border-color: #cbd5e1;
  }

  .person-card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    .person-avatar {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: rgba(var(--v-primary-base), 0.08);
      display: flex;
      align-items: center;
      justify-content: center;

      .v-icon {
        font-size: 1.5rem !important;
      }
    }

    .person-header-text {
      display: flex;
      flex-direction: column;

      .person-title {
        font-size: 1.1rem;
        font-weight: 800;
        color: #0f172a;
      }

      .person-subtitle {
        font-size: 0.75rem;
        font-weight: 600;
        color: #64748b;
      }
    }
  }
}

.delete-person-btn {
  background: #fef2f2 !important;
  border: 1px solid #fee2e2;
  transition: all 0.2s ease;

  &:hover {
    background: #fee2e2 !important;
    transform: scale(1.05);
  }
}

/* Dashed Add Person Card */
.add-person-container {
  margin-bottom: 32px;
}

.dashed-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e1;
  background-color: rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 20px;
  cursor: pointer;
  font-weight: 700;
  color: #475569;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
    background-color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.03);

    .v-icon {
      transform: scale(1.1);
    }
  }

  .v-icon {
    font-size: 1.4rem !important;
    transition: transform 0.2s ease;
  }
}

/* Input Overrides */
.premium-input {
  ::v-deep input {
    color: #3b3e50 !important;
  }
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 12px !important;

      transition: all 0.3s ease !important;
      border: 1px solid #e2e8f0;

      &:hover {
        background-color: #ffffff !important;
        border-color: #cbd5e1;
      }

      &::before,
      &::after {
        display: none !important;
      }
    }
  }

  &.v-input--is-focused {
    ::v-deep .v-input__control {
      .v-input__slot {
        background-color: #ffffff !important;
        border-color: var(--v-primary-base) !important;
        box-shadow: 0 0 0 3px rgba(var(--v-primary-base), 0.15) !important;
      }
    }
  }
}

/* Room Selection Container Grid */
.room-selection-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 28px;
  margin-bottom: 32px;
  width: 100%;
}

.no-results {
  text-align: center;
  padding: 60px 20px;
  background-color: #ffffff;
  border-radius: 24px;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  margin-bottom: 32px;
}

/* Luxury Room Card */
.luxury-room-card {
  background: #ffffff;
  border-radius: 28px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 4px 15px rgba(15, 23, 42, 0.01);
  position: relative;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.06);
    border-color: #cbd5e1;
  }

  &.selected-glow {
    border-color: var(--v-primary-base);
    box-shadow: 0 0 0 2px var(--v-primary-base), 0 20px 40px rgba(var(--v-primary-base), 0.15);

    .card-select-action {
      .select-btn {
        background-color: var(--v-primary-base) !important;
        color: #ffffff !important;
        font-weight: 850;
      }
    }
  }

  .room-image-wrapper {
    position: relative;
    overflow: hidden;
  }

  .room-image {
    transition: transform 0.6s ease;
  }

  &:hover .room-image {
    transform: scale(1.04);
  }

  .room-size-tag {
    position: absolute;
    bottom: 16px;
    left: 16px;
    background: rgba(15, 23, 42, 0.7);
    backdrop-filter: blur(8px);
    padding: 6px 12px;
    border-radius: 50px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #ffffff;
    display: flex;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .room-selected-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: var(--v-primary-base);
    border: 2px solid #ffffff;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  .room-info-wrapper {
    padding: 28px;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .room-header {
    margin-bottom: 20px;
    .room-category-label {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--v-primary-base);
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    .room-card-title {
      font-size: 1.35rem;
      font-weight: 800;
      color: #0f172a;
      line-height: 1.25;
      margin-top: 4px;
    }
  }

  .room-card-price {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    background-color: #f8fafc;
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 20px;

    .price-box {
      display: flex;
      flex-direction: column;
    }

    .price-value {
      font-size: 1.5rem;
      font-weight: 900;
      color: #0f172a;
      line-height: 1.1;
    }

    .price-period {
      font-size: 0.75rem;
      color: #64748b;
      font-weight: 600;
      margin-top: 2px;
    }

    .price-daily {
      display: flex;
      flex-direction: column;
      text-align: right;
    }

    .daily-value {
      font-size: 1.05rem;
      font-weight: 750;
      color: var(--v-primary-base);
    }

    .daily-label {
      font-size: 0.7rem;
      color: #64748b;
      font-weight: 600;
    }
  }

  .details-divider {
    height: 1px;
    background-color: #edf2f7;
    margin-bottom: 16px;
  }

  .details-trigger-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
    font-weight: 700;
    color: #475569;
    padding: 4px 0;

    &:hover {
      color: var(--v-primary-base);
    }

    .details-arrow-icon {
      font-size: 1.25rem !important;
      transition: transform 0.3s ease;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .room-equipment-expanded {
    margin-top: 16px;
    padding-top: 4px;

    .equipment-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }

    .equip-item {
      display: flex;
      align-items: center;
      font-size: 0.78rem;
      font-weight: 500;
      color: #475569;

      .v-icon {
        font-size: 1.05rem !important;
      }
    }
  }

  .card-select-action {
    margin-top: 24px;

    .select-btn {
      height: 48px !important;
      font-size: 0.9rem !important;
      font-weight: 700 !important;
      letter-spacing: 0.5px !important;

      &.selected-btn {
        background-color: var(--v-primary) !important;
        color: white !important;
      }
    }
  }
}

/* Confirmation Stage layout */
.confirmation-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 32px;
  margin-bottom: 40px;
}

.receipt-card {
  background-color: #ffffff;
  border-radius: 28px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.02);
  overflow: hidden;

  .receipt-header {
    background-color: #0f172a;
    padding: 36px 24px;
    color: #ffffff;
    display: flex;
    flex-direction: column;
    align-items: center;

    .success-ring-glow {
      width: 68px;
      height: 68px;
      border-radius: 50%;
      background: rgba(var(--v-primary-base), 0.15);
      border: 2px solid var(--v-primary-base);
      box-shadow: 0 0 25px rgba(var(--v-primary-base), 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
    }

    .receipt-title {
      font-size: 1.6rem;
      font-weight: 850;
      letter-spacing: -0.5px;
    }

    .receipt-subtitle {
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.6);
      margin-top: 4px;
      margin-bottom: 0;
    }
  }

  .receipt-body {
    padding: 32px;
  }

  .receipt-section-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 16px;
  }

  .receipt-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 0.95rem;

    .row-label {
      color: #64748b;
      font-weight: 500;
    }

    .row-value {
      color: #0f172a;
      font-weight: 650;

      &.bold-value {
        font-weight: 800;
      }
    }
  }

  .receipt-person-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f8fafc;
    padding: 8px 16px;
    border-radius: 12px;
    margin-bottom: 8px;
    font-size: 0.9rem;

    .person-receipt-info {
      display: flex;
      align-items: center;
    }

    .receipt-person-name {
      font-weight: 700;
      color: #0f172a;
    }

    .receipt-person-details {
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
    }
  }

  .receipt-room-box {
    display: flex;
    align-items: center;
    background-color: #f8fafc;
    padding: 12px;
    border-radius: 16px;
    border: 1px solid #e2e8f0;

    .receipt-room-details {
      display: flex;
      flex-direction: column;

      .receipt-room-cat {
        font-size: 0.7rem;
        font-weight: 700;
        color: var(--v-primary-base);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .receipt-room-name {
        font-size: 1.1rem;
        font-weight: 800;
        color: #0f172a;
      }
    }
  }
}

.invoice-breakdown {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  margin-top: 24px;

  .invoice-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.95rem;
    margin-bottom: 10px;

    .invoice-label {
      color: #475569;
      font-weight: 500;
    }

    .invoice-value {
      color: #0f172a;
      font-weight: 700;
    }

    &.total-row {
      align-items: flex-start;
      margin-bottom: 0;

      .invoice-total-label {
        font-size: 1.1rem;
        font-weight: 850;
        color: #0f172a;
        margin-top: 6px;
      }

      .invoice-total-value {
        font-size: 1.5rem;
        margin-top: 5px;
        font-weight: 900;
        color: var(--v-primary-base);
        line-height: 1;
      }

      .invoice-tax-info {
        display: block;
        font-size: 0.7rem;
        color: #64748b;
        font-weight: 600;
        margin-top: 4px;
      }
    }
  }
}

.trust-benefits-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: flex-start;

  .panel-headline {
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 4px;
  }

  .benefit-card-item {
    display: flex;
    gap: 16px;
    background-color: #ffffff;
    padding: 20px;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.01);

    .benefit-icon-ring {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background-color: #f8fafc;
      border: 1px solid #cbd5e1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      .v-icon {
        font-size: 1.3rem !important;
      }
    }

    .benefit-texts {
      h4 {
        font-size: 0.95rem;
        font-weight: 750;
        color: #0f172a;
        margin-bottom: 4px;
      }
      p {
        font-size: 0.8rem;
        color: #475569;
        line-height: 1.4;
        margin-bottom: 0;
      }
    }
  }
}

.checkout-actions-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
  padding-top: 32px;
  margin-top: 8px;

  .checkout-back-btn {
    font-weight: 700;
  }

  .ultimate-checkout-btn {
    height: 60px !important;
    padding: 0 48px !important;
    font-size: 1.05rem !important;
    font-weight: 850 !important;
    letter-spacing: 0.5px !important;
    border-radius: 50px !important;
    animation: dynamic-glow 3s infinite;
  }
}

@keyframes dynamic-glow {
  0% {
    transform: scale(1);
    box-shadow: 0 10px 25px rgba(var(--v-primary-base), 0.3);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 15px 35px rgba(var(--v-primary-base), 0.55);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 10px 25px rgba(var(--v-primary-base), 0.3);
  }
}

/* Slide standard footer/actions (used in slides 0, 1) */
.slide-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
  padding-top: 32px;
  margin-top: auto;

  .next-step-btn {
    height: 52px !important;
    padding: 0 32px !important;
    font-size: 0.95rem !important;
    font-weight: 750 !important;
    letter-spacing: 0.5px !important;
    border-radius: 50px !important;
    box-shadow: 0 10px 20px rgba(var(--v-primary-base), 0.25) !important;
    transition: all 0.3s ease !important;

    &:hover:not(.v-btn--disabled) {
      transform: translateY(-2px);
      box-shadow: 0 15px 25px rgba(var(--v-primary-base), 0.35) !important;
    }
  }
}

/* Animations */
.animate-blur-in {
  animation: blurIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes blurIn {
  from {
    opacity: 0;
    transform: translateY(15px);
    filter: blur(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

/* Hide Swiper Pagination */
::v-deep .swiper-pagination {
  display: none !important;
}

/* Responsiveness for iPad/Mobile */
@media (max-width: 960px) {
  .booking-layout-wrapper {
    flex-direction: column;
    height: 100vh;
    overflow-y: auto;
  }

  .booking-showcase-sidebar {
    display: none;
  }

  .mobile-header {
    display: flex;
    align-items: center;
    padding: 16px 24px;
    background-color: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 100;

    .mobile-brand-title {
      font-size: 1.15rem;
      font-weight: 800;
      color: #0f172a;
    }
  }

  .booking-progress-tracker {
    padding: 16px 24px;
    .progress-step {
      .step-label {
        display: none; /* Hide labels on mobile to save space */
      }
    }
  }

  .slide-content {
    padding: 24px 20px;
  }

  .slide-title {
    font-size: 1.8rem;
    margin-bottom: 24px;
  }

  .confirmation-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .receipt-card {
    .receipt-body {
      padding: 20px;
    }
  }

  .checkout-actions-wrapper {
    flex-direction: column-reverse;
    gap: 16px;
    align-items: stretch;

    .ultimate-checkout-btn {
      width: 100%;
    }
  }

  .room-selection-container {
    grid-template-columns: 1fr;
  }
}
</style>
